import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theSmokeData } from '../data/theSmokeData';
import { ArtistSection } from './ArtistSection';
import SmokeBackground from './SmokeBackground';
import { ExhibitionLayout } from './layouts/ExhibitionLayout';
import { ExhibitionNavigation } from './navigation/ExhibitionNavigation';

const TheSmoke = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const [activeArtist, setActiveArtist] = useState<string | null>(artistId || null);
  const [isScrolling, setIsScrolling] = useState(false);

  // Initial scroll to artist section when directly accessing URL
  useEffect(() => {
    if (artistId) {
      const element = document.getElementById(artistId);
      if (element) {
        setIsScrolling(true);
        element.scrollIntoView({ behavior: 'instant' });
        setActiveArtist(artistId);
        setTimeout(() => setIsScrolling(false), 1000);
      }
    }
  }, []); // Empty dependency array for initial load only

  const scrollToArtist = useCallback((artistId: string) => {
    const element = document.getElementById(artistId);
    if (element) {
      setIsScrolling(true);
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveArtist(artistId);
      navigate(`/exhibitions/the-smoke/${artistId}`);
      // Reset scrolling state after animation
      setTimeout(() => setIsScrolling(false), 1000);
    }
  }, [navigate]);

  // Update active artist on scroll
  useEffect(() => {
    let scrollTimeout: number;

    const handleScroll = () => {
      if (isScrolling) return;

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const sections = theSmokeData.map(artist => ({
          id: artist.id,
          element: document.getElementById(artist.id),
        }));

        const currentSection = sections.find(section => {
          if (!section.element) return false;
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        });

        if (currentSection && currentSection.id !== activeArtist) {
          setActiveArtist(currentSection.id);
          navigate(`/exhibitions/the-smoke/${currentSection.id}`);
        }
      }, 100); // Debounce scroll events
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [navigate, isScrolling, activeArtist]);

  // Handle artistId changes from URL
  useEffect(() => {
    if (artistId && artistId !== activeArtist) {
      const element = document.getElementById(artistId);
      if (element) {
        setIsScrolling(true);
        element.scrollIntoView({ behavior: 'smooth' });
        setActiveArtist(artistId);
        setTimeout(() => setIsScrolling(false), 1000);
      }
    }
  }, [artistId, activeArtist]);

  const navigationItems = theSmokeData.map(artist => ({
    id: artist.id,
    name: artist.name,
  }));

  return (
    <ExhibitionLayout
      title="The Smoke"
      subtitle="Exploring the theme of Burning season and how it affects our physical and emotional health"
      openingDate="MARCH 7, 2025"
      closingDate="MAY 26, 2025"
      background={<SmokeBackground />}
      navigation={
        <ExhibitionNavigation
          items={navigationItems}
          activeId={activeArtist}
          onItemClick={scrollToArtist}
        />
      }
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1rem' }}>
        {theSmokeData.map(artist => (
          <ArtistSection key={artist.id} artist={artist} />
        ))}
      </div>
    </ExhibitionLayout>
  );
};

export default TheSmoke; 